import { entityGenerateUserMessageDisplayContent } from "~shared/agenticChat/entityGenerateUserMessageContent";
import {
  branchTipMessageId,
  linearMessagesForDisplay,
  linearPathToLeaf,
  userBranchVariantsAtParent,
  userMessageIdsWithVisibleAssistantReply,
} from "~shared/persistentChatBranching";
import { PersistentChatMessageData, PersistentChatWithMessages } from "~shared/types/persistent-chat-types";

import { fetchFullChatForCopy } from "./PersistentChatQueries";
import { sanitizeChatTitleForDisplay } from "./chatTitleUtils";
import { extractPlainTextFromMentions, stripTrailingDocumentMentionsFromDisplay } from "./mentions/mentionFormatUtils";
import { normalizeParentheticalCommentLinks, stripThinkTagsFromAssistantOutput } from "./streamingMarkdown";

const USER_LABEL = "You";
const ASSISTANT_LABEL = "Sudowrite";

export type ChatConversationCopySection = {
  label: string;
  messages: PersistentChatMessageData[];
};

function normalizeMessageContent(content: unknown): string {
  if (typeof content === "string") return content;
  if (content == null) return "";
  if (typeof content === "number" || typeof content === "boolean" || typeof content === "bigint") {
    return String(content);
  }
  try {
    const serialized = JSON.stringify(content);
    if (typeof serialized === "string") return serialized;
  } catch {
    // Fall through to generic conversion.
  }
  return String(content);
}

function shouldSkipMessageForCopy(message: PersistentChatMessageData, index: number, messages: PersistentChatMessageData[]): boolean {
  if (message.metadata?.hiddenFromUI) return true;
  if (message.role === "user" && message.metadata?.planApproval) return true;

  if (message.role === "assistant") {
    if (message.metadata?.helpMenuGreeting) return true;
    if (message.metadata?.source === "help_menu_agentic") return true;
  }

  if (message.role === "user" && index > 0) {
    const prevMessage = messages[index - 1];
    if (prevMessage?.metadata?.questionAsked) {
      const prevRawIdx = messages.findIndex((m) => m.id === prevMessage.id);
      const currRawIdx = messages.findIndex((m) => m.id === message.id);
      const hasHiddenAnswerBetween =
        prevRawIdx !== -1
        && currRawIdx !== -1
        && messages.slice(prevRawIdx + 1, currRawIdx).some((m) => m.metadata?.hiddenFromUI);
      if (!hasHiddenAnswerBetween) return true;
    }
  }

  return false;
}

function getQuestionAnswerAppend(
  message: PersistentChatMessageData,
  index: number,
  messages: PersistentChatMessageData[],
): { text: string; stripAppendedDocumentMentions: boolean } | null {
  if (message.role !== "assistant" || !message.metadata?.questionAsked) return null;

  const preview = (message.metadata as { questionAnswerPreview?: string })?.questionAnswerPreview;
  if (preview?.trim()) {
    return {
      text: preview.trim(),
      stripAppendedDocumentMentions:
        (message.metadata as { questionAnswerPreviewHasAppendedDocumentMentions?: boolean })
          ?.questionAnswerPreviewHasAppendedDocumentMentions === true,
    };
  }

  const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;
  if (nextMessage?.role === "user" && nextMessage.content?.trim()) {
    return {
      text: nextMessage.content.trim(),
      stripAppendedDocumentMentions: nextMessage.metadata?.appendedDocumentMentions === true,
    };
  }

  return null;
}

function formatQuestionAnswerForCopy(append: { text: string; stripAppendedDocumentMentions: boolean }): string {
  const displayContent = stripTrailingDocumentMentionsFromDisplay(
    append.text,
    append.stripAppendedDocumentMentions,
  );
  return extractPlainTextFromMentions(displayContent).trim();
}

export function getCopyableMessageText(message: PersistentChatMessageData, index: number, messages: PersistentChatMessageData[]): string {
  const rawContent = normalizeMessageContent(message.content);

  if (message.role === "user") {
    const displayContent = stripTrailingDocumentMentionsFromDisplay(
      entityGenerateUserMessageDisplayContent(rawContent, message.metadata as { ctaActionType?: string; generateInputs?: unknown }),
      message.metadata?.appendedDocumentMentions === true,
    );
    return extractPlainTextFromMentions(displayContent).trim();
  }

  if (message.role === "assistant") {
    const normalized = normalizeParentheticalCommentLinks(stripThinkTagsFromAssistantOutput(rawContent)).trim();
    const answerAppend = getQuestionAnswerAppend(message, index, messages);
    if (!answerAppend) return normalized;
    const answerText = formatQuestionAnswerForCopy(answerAppend);
    if (!answerText) return normalized;
    if (!normalized) return answerText;
    return `${normalized}\n\n${answerText}`;
  }

  return extractPlainTextFromMentions(rawContent).trim();
}

function formatMessageSection(messages: PersistentChatMessageData[]): string {
  const parts: string[] = [];

  messages.forEach((message, index) => {
    if (shouldSkipMessageForCopy(message, index, messages)) return;

    const text = getCopyableMessageText(message, index, messages);
    if (!text) return;

    const label = message.role === "user" ? USER_LABEL : message.role === "assistant" ? ASSISTANT_LABEL : null;
    if (!label) return;

    parts.push(`${label}:`, text, "");
  });

  return parts.join("\n").trim();
}

function visibleUserBranchVariantsAtParent(
  allMessages: PersistentChatMessageData[],
  parentId: string | null,
  visibleReplyUserIds: Set<string>,
): PersistentChatMessageData[] {
  return userBranchVariantsAtParent(allMessages, parentId).filter((candidate) =>
    visibleReplyUserIds.has(candidate.id),
  );
}

function collectAlternateBranchSectionsForPath(
  allMessages: PersistentChatMessageData[],
  path: PersistentChatMessageData[],
  visibleReplyUserIds: Set<string>,
  shownMessageIds: Set<string>,
  sections: ChatConversationCopySection[],
): void {
  const pathUserIds = new Set(path.filter((message) => message.role === "user").map((message) => message.id));

  for (const message of path) {
    if (message.role !== "user") continue;

    const parentId = message.parent_message_id ?? null;
    const variants = visibleUserBranchVariantsAtParent(allMessages, parentId, visibleReplyUserIds);
    if (variants.length <= 1) continue;

    variants.forEach((variant, variantIndex) => {
      if (pathUserIds.has(variant.id) || shownMessageIds.has(variant.id)) return;

      const tipId = branchTipMessageId(allMessages, variant.id);
      const branchPath = linearPathToLeaf(allMessages, tipId);
      const forkIndex = branchPath.findIndex((row) => row.id === variant.id);
      const branchMessages = forkIndex >= 0 ? branchPath.slice(forkIndex) : branchPath;
      if (branchMessages.length === 0) return;

      branchMessages.forEach((row) => shownMessageIds.add(row.id));
      sections.push({
        label: `Branch ${variantIndex + 1} of ${variants.length}`,
        messages: branchMessages,
      });

      collectAlternateBranchSectionsForPath(
        allMessages,
        branchMessages,
        visibleReplyUserIds,
        shownMessageIds,
        sections,
      );
    });
  }
}

export function collectAlternateBranchSections(
  allMessages: PersistentChatMessageData[],
  activePath: PersistentChatMessageData[],
): ChatConversationCopySection[] {
  const visibleReplyUserIds = userMessageIdsWithVisibleAssistantReply(allMessages);
  const shownMessageIds = new Set(activePath.map((message) => message.id));
  const sections: ChatConversationCopySection[] = [];

  collectAlternateBranchSectionsForPath(
    allMessages,
    activePath,
    visibleReplyUserIds,
    shownMessageIds,
    sections,
  );

  return sections;
}

export function formatChatConversationPlainText(
  messages: PersistentChatMessageData[],
  title?: string | null,
  options?: { sections?: ChatConversationCopySection[] },
): string {
  const parts: string[] = [];
  const trimmedTitle = typeof title === "string" ? title.trim() : "";
  if (trimmedTitle) {
    parts.push(trimmedTitle, "");
  }

  const activeSection = formatMessageSection(messages);
  if (activeSection) {
    parts.push(activeSection);
  }

  for (const section of options?.sections ?? []) {
    const sectionText = formatMessageSection(section.messages);
    if (!sectionText) continue;
    parts.push("", `--- ${section.label} ---`, "", sectionText);
  }

  return parts.join("\n").trim();
}

export function formatChatConversationForCopy(
  chat: Pick<PersistentChatWithMessages, "messages" | "active_leaf_message_id" | "title">,
  titleOverride?: string | null,
): string {
  const allMessages = chat.messages ?? [];
  const activePath = linearMessagesForDisplay(allMessages, chat.active_leaf_message_id);
  const alternateSections = collectAlternateBranchSections(allMessages, activePath);
  const displayTitle = sanitizeChatTitleForDisplay(titleOverride ?? chat.title);
  return formatChatConversationPlainText(activePath, displayTitle, {
    sections: alternateSections,
  });
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!text) return false;

  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to execCommand fallback.
    }
  }

  if (typeof document === "undefined" || typeof document.execCommand !== "function") return false;

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "absolute";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  const successful = document.execCommand("copy");
  document.body.removeChild(textArea);
  return successful;
}

export type CopyChatConversationResult = {
  plainText: string;
  copied: boolean;
  messageCount: number;
};

export async function copyChatConversation(chatId: string, title?: string | null): Promise<CopyChatConversationResult> {
  const chat = await fetchFullChatForCopy(chatId);
  const plainText = formatChatConversationForCopy(chat, title);
  const copied = plainText.trim() ? await copyTextToClipboard(plainText) : false;
  return {
    plainText,
    copied,
    messageCount: chat.messages?.length ?? 0,
  };
}
